import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CITIES, type City, type Language } from '@pomoshnik/shared';
import { useAuthStore } from '@/lib/stores/authStore';
import { LangCityScreenView } from './LangCityScreenView';

/**
 * Container Stage 1 онбординга (`/onboarding/lang-city`).
 *
 * State machine: idle → submitting → success (redirect /). Error state
 * для Stage 1 практически невозможен — `setLangCity` синхронный, без
 * сетевого запроса. Единственный возможный sync-throw — `RegionImmutableError`
 * (P-008) если пользователь уже зарегистрирован и пытается выбрать город
 * другого региона; в Phase 2 это маловероятный edge case (LangCityScreen
 * редко достигается registered-юзером), но Container ловит и показывает
 * inline error.
 *
 * P-019 vertical buttons: один submit-button под формой.
 *
 * Pre-fill из authStore: если lang/city уже установлены (вернувшийся guest),
 * Select'ы предзаполняются — пользователь может изменить или просто submit.
 */
export function LangCityScreen() {
  const navigate = useNavigate();
  const initialLang = useAuthStore((s) => s.lang);
  const initialCity = useAuthStore((s) => s.city);
  const setLangCity = useAuthStore((s) => s.setLangCity);

  const [selectedLang, setSelectedLang] = useState<Language | ''>(initialLang ?? '');
  const [selectedCity, setSelectedCity] = useState<City | ''>(initialCity ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const cityOptions = CITIES.map((c) => ({ value: c, label: c }));

  const handleSubmit = () => {
    if (selectedLang === '' || selectedCity === '') {
      setValidationError('Выберите язык и город');
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    try {
      setLangCity(selectedLang, selectedCity);
      navigate('/');
    } catch (err) {
      setSubmitting(false);
      // RegionImmutableError — единственный ожидаемый sync throw из setLangCity.
      // Безопасный fallback: подсказать пользователю, что регион зафиксирован.
      const message =
        err instanceof Error
          ? 'Регион пользователя зафиксирован — выберите город из своего региона'
          : 'Ошибка сохранения. Попробуйте позже.';
      setValidationError(message);
    }
  };

  return (
    <LangCityScreenView
      cityOptions={cityOptions}
      selectedLang={selectedLang}
      selectedCity={selectedCity}
      onLangChange={setSelectedLang}
      onCityChange={setSelectedCity}
      onSubmit={handleSubmit}
      validationError={validationError}
      submitting={submitting}
    />
  );
}
